import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import RoadSign from "@/components/RoadSign";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lock, Gauge, Wine, Coins, ShieldAlert, Lightbulb, Phone, Info, Trophy } from "lucide-react";

const RULE_ICON = {
  vitesse_ville: Gauge, vitesse_route: Gauge, vitesse_voie_rapide: Gauge, vitesse_autoroute: Gauge,
  alcool: Wine, peage: Coins, equipement: ShieldAlert, feux: Lightbulb, telephone: Phone, particularite: Info,
};

export default function CountryDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [country, setCountry] = useState(null);
  const [signs, setSigns] = useState([]);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    api.get(`/countries/${code}`).then(({ data }) => setCountry(data)).catch(() => {});
    api.get(`/signs?country=${code}`)
      .then(({ data }) => { setSigns(data); setLocked(false); })
      .catch((err) => {
        if (err.response?.status === 402 || err.response?.status === 401) {
          setLocked(true);
          api.get(`/signs/preview?country=${code}`).then(({ data }) => setSigns(data)).catch(() => {});
        }
      });
  }, [code]);

  if (!country) return <div className="min-h-[60vh]" />;

  return (
    <div className="max-w-7xl mx-auto px-5 py-12 paper-noise min-h-[70vh]">
      <div className="flex items-center gap-3 mb-2">
        <span className="text-4xl">{country.flag}</span>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tighter text-[#2C2A28]">{country.name}</h1>
      </div>
      <p className="text-[#5C5854] max-w-2xl mb-10">{country.tagline}</p>

      <h2 className="font-serif text-3xl tracking-tight text-[#2C2A28] mb-6">{t("country.rulesTitle")}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-14">
        {Object.entries(country.rules || {}).map(([k, v], i) => {
          const Icon = RULE_ICON[k] || Info;
          return (
            <motion.div key={k} className="bg-white rounded-2xl border border-[#E5E0D8] p-5 tc-card-hover"
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}
              data-testid={`rule-${k}`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4 text-[#E06D53]" />
                <span className="text-xs uppercase tracking-widest text-[#8C857B]">{t(`country.rule.${k}`, k)}</span>
              </div>
              <p className="text-[#2C2A28] font-medium leading-snug">{v}</p>
            </motion.div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-serif text-3xl tracking-tight text-[#2C2A28]">{t("country.signsTitle")}</h2>
        {!locked && (
          <Button onClick={() => navigate(`/quiz?country=${code}`)} className="rounded-full bg-[#1D4E89] hover:bg-[#173d6b] text-white" data-testid="country-quiz-btn">
            <Trophy className="w-4 h-4 mr-2" /> {t("country.quizBtn")} {country.name}
          </Button>
        )}
      </div>

      {locked && (
        <div className="bg-[#FBEDE9] border border-[#F2CFC5] rounded-2xl p-6 mb-8 flex items-start gap-4" data-testid="paywall-banner">
          <Lock className="w-6 h-6 text-[#E06D53] shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="font-medium text-[#2C2A28] text-lg">{t("country.paywallTitle")}</h3>
            <p className="text-sm text-[#5C5854] mt-1">{t("country.paywallDesc")}</p>
            <Button onClick={() => navigate("/tarifs")} className="rounded-full bg-[#E06D53] hover:bg-[#C95B42] text-white mt-4" data-testid="paywall-cta">
              {t("country.paywallCta")}
            </Button>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {signs.map((sign, i) => (
          <motion.div key={sign.id} className="bg-white rounded-2xl border border-[#E5E0D8] p-6 flex flex-col items-center text-center tc-card-hover"
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}
            data-testid={`sign-card-${sign.id}`}>
            <RoadSign type={sign.type} value={sign.value} country={sign.country} size={90} />
            <Badge className="mt-4 bg-[#FBEDE9] text-[#C95B42] hover:bg-[#FBEDE9] rounded-full">{sign.category}</Badge>
            <h4 className="font-medium text-[#2C2A28] mt-3">{sign.name}</h4>
            <p className="text-sm text-[#5C5854] mt-2 leading-relaxed">{sign.description}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
